// Pure distance/course contract. Backend payloads are authoritative; these
// constants are the legacy-safe fallback and the renderer mapping audited
// against og-games-api by test-horseracing-parity.mjs.

export const DEFAULT_DISTANCE_METERS = 600;
export const MIN_DISTANCE_METERS = 100;
export const MAX_DISTANCE_METERS = 3000;
export const DISTANCE_STEP_METERS = 100;
export const LAP_DISTANCE_METERS = 1000;

// Existing Rose Highlands geometry. The finish remains at the grandstand.
export const COURSE_LOOP_UNITS = 920;
export const COURSE_STRAIGHT_UNITS = 280;
export const LEGACY_RACE_START_WORLD = 467.9;
export const FINISH_WORLD = (
  LEGACY_RACE_START_WORLD + DEFAULT_DISTANCE_METERS
) % COURSE_LOOP_UNITS;
export const GATE_AHEAD_WORLD_UNITS = 1.5;

const mod = (value, modulus) => ((value % modulus) + modulus) % modulus;

export function validateDistanceMeters(
  value,
  {
    min = MIN_DISTANCE_METERS,
    max = MAX_DISTANCE_METERS,
    step = DISTANCE_STEP_METERS,
  } = {},
) {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new TypeError('distanceMeters must be a finite integer');
  }
  if (value < min || value > max) {
    throw new RangeError(`distanceMeters must be between ${min} and ${max}`);
  }
  if (value % step !== 0) {
    throw new RangeError(`distanceMeters must be divisible by ${step}`);
  }
  return value;
}

export function safeDistanceMeters(value, fallback = DEFAULT_DISTANCE_METERS) {
  try {
    return validateDistanceMeters(Number(value));
  } catch (_) {
    return validateDistanceMeters(fallback);
  }
}

export function raceCourseGeometry({
  distanceMeters = DEFAULT_DISTANCE_METERS,
  lapDistanceMeters = LAP_DISTANCE_METERS,
  legacy = false,
} = {}) {
  const distance = validateDistanceMeters(Number(distanceMeters));
  const lap = Number(lapDistanceMeters);
  if (!Number.isFinite(lap) || !Number.isInteger(lap)
    || lap < DISTANCE_STEP_METERS || lap % DISTANCE_STEP_METERS !== 0) {
    throw new TypeError('lapDistanceMeters must be a positive 100m increment');
  }
  const worldUnitsPerMeter = legacy ? 1 : COURSE_LOOP_UNITS / lap;
  const startWorld = legacy
    ? LEGACY_RACE_START_WORLD
    : mod(FINISH_WORLD - distance * worldUnitsPerMeter, COURSE_LOOP_UNITS);
  const lapCount = Math.max(1, Math.ceil(distance / lap));
  const partialFirstLapMeters = distance % lap;
  return Object.freeze({
    distanceMeters: distance,
    lapDistanceMeters: lap,
    lapCount,
    fullLaps: Math.floor(distance / lap),
    partialFirstLapMeters,
    hasPartialLap: partialFirstLapMeters !== 0,
    startTrackMeters: mod(-distance, lap),
    startWorld,
    gateWorld: mod(
      startWorld + GATE_AHEAD_WORLD_UNITS,
      COURSE_LOOP_UNITS,
    ),
    finishWorld: FINISH_WORLD,
    worldUnitsPerMeter,
    legacy,
  });
}

export function raceMetersToWorld(raceMeters, geometry) {
  return geometry.startWorld + Number(raceMeters) * geometry.worldUnitsPerMeter;
}

export function trackMetersToWorld(trackMeters, geometry) {
  return geometry.finishWorld + Number(trackMeters) * (
    COURSE_LOOP_UNITS / geometry.lapDistanceMeters
  );
}

export function raceProgress(distanceTravelled, geometry) {
  const raw = Number.isFinite(Number(distanceTravelled))
    ? Number(distanceTravelled)
    : 0;
  const meters = Math.max(0, Math.min(geometry.distanceMeters, raw));
  const lap = meters >= geometry.distanceMeters
    ? geometry.lapCount
    : Math.min(
      geometry.lapCount,
      Math.floor(meters / geometry.lapDistanceMeters) + 1,
    );
  return {
    meters,
    lap,
    lapCount: geometry.lapCount,
    remainingMeters: Math.max(0, geometry.distanceMeters - meters),
  };
}

export function markerTrackMeters({
  lapDistanceMeters = LAP_DISTANCE_METERS,
  stepMeters = DISTANCE_STEP_METERS,
  mobile = false,
} = {}) {
  const stride = mobile ? stepMeters * 2 : stepMeters;
  const out = [];
  for (let meters = stride; meters < lapDistanceMeters; meters += stride) {
    out.push(meters);
  }
  return out;
}

// World signs follow the current leader/broadcast lap. A Ride HUD can show
// the selected horse independently, but one physical sign cannot describe
// two horses spread across a lap seam.
export function markerLabelForLeader(markerMeters, leaderDistance, geometry) {
  let firstCrossing = mod(
    markerMeters - geometry.startTrackMeters,
    geometry.lapDistanceMeters,
  );
  // A sign exactly at the gate is not a misleading "0m" marker. Its first
  // legal label is the next full-lap crossing.
  if (firstCrossing === 0) firstCrossing = geometry.lapDistanceMeters;
  const leaderLapIndex = Math.min(
    geometry.lapCount - 1,
    Math.floor(Math.max(0, Number(leaderDistance) || 0) / geometry.lapDistanceMeters),
  );
  const cumulativeMeters = firstCrossing
    + leaderLapIndex * geometry.lapDistanceMeters;
  if (cumulativeMeters <= 0 || cumulativeMeters > geometry.distanceMeters) {
    return null;
  }
  return `${cumulativeMeters}m`;
}

export function distanceDescription(distanceMeters, lapDistanceMeters = LAP_DISTANCE_METERS) {
  const geometry = raceCourseGeometry({ distanceMeters, lapDistanceMeters });
  if (!geometry.hasPartialLap) {
    return `${geometry.distanceMeters}m · ${geometry.fullLaps} ${
      geometry.fullLaps === 1 ? 'lap' : 'laps'
    }`;
  }
  if (geometry.fullLaps === 0) return `${geometry.distanceMeters}m · partial lap`;
  return `${geometry.distanceMeters}m · ${geometry.fullLaps} full + ${
    geometry.partialFirstLapMeters
  }m`;
}
